import { Menu, X } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router';
import { useLanguage } from '@/contexts/LanguageContext';
import { useCategory } from '@/contexts/CategoryContext';
import { useAuth } from '@/contexts/AuthContext';
import { useSettings } from '@/contexts/SettingsContext';
import { getAllCategories, type Category } from '@/services/api';

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProjectsDropdownOpen, setIsProjectsDropdownOpen] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const { language, setLanguage, t } = useLanguage();
  const { setSelectedCategory } = useCategory();
  const { user } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const loadCategories = async () => {
      const data = await getAllCategories();
      setCategories(data);
    };
    loadCategories();
  }, []);

  const handleCategoryClick = (categoryId: string) => {
    setSelectedCategory(categoryId as any);
    setIsProjectsDropdownOpen(false);
    navigate('/');
  };

  const handleLogoClick = () => {
    // Clear saved scroll position when clicking logo
    sessionStorage.removeItem('projectsScrollY');
  };

  return (
    <header className="fixed top-0 left-0 right-0 bg-white border-b border-gray-200" style={{ zIndex: 9999 }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            onClick={handleLogoClick}
            className="text-2xl md:text-2xl tracking-tight shrink-0 font-normal" 
            style={{ fontSize: '1.35rem' }}
          >
            <span className="font-semibold">ZSALAKÓ</span> ÉPÍTŐMŰHELY
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8 lg:gap-24 shrink min-w-0">
            <nav className={`flex items-center gap-3 lg:gap-5 shrink min-w-0 ${!settings.englishEnabled ? 'ml-auto' : ''}`}>
              {settings.newsEnabled && (
                <Link 
                  to="/news" 
                  className={`${location.pathname === '/news' ? 'text-gray-900' : 'text-gray-400'} hover:text-black transition-colors whitespace-nowrap`} 
                  style={{ fontSize: '1.1rem' }}
                >
                  {t('nav.news')}
                </Link>
              )}
              
              {/* Projects with Dropdown */}
              <div 
                className="relative"
                onMouseEnter={() => setIsProjectsDropdownOpen(true)}
                onMouseLeave={() => setIsProjectsDropdownOpen(false)}
              >
                <Link 
                  to="/" 
                  className={`${location.pathname === '/' ? 'text-gray-900' : 'text-gray-400'} hover:text-black transition-colors whitespace-nowrap`}
                  style={{ fontSize: '1.1rem' }}
                  onClick={() => {
                    setSelectedCategory('all');
                    setIsProjectsDropdownOpen(false);
                  }}
                >
                  {t('nav.projects')}
                </Link>
                
                {/* Dropdown Menu */}
                {isProjectsDropdownOpen && (
                  <div className="absolute top-full left-0 pt-2">
                    <div className="w-48 bg-white border border-gray-200 shadow-lg">
                      {categories.map((category) => (
                        <button
                          key={category.id}
                          onClick={() => handleCategoryClick(category.id)}
                          className="w-full text-left px-4 py-3 text-gray-400 hover:text-black hover:bg-gray-50 transition-colors text-sm uppercase"
                        >
                          {category.name[language]}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <Link 
                to="/about" 
                className={`${location.pathname === '/about' ? 'text-gray-900' : 'text-gray-400'} hover:text-black transition-colors whitespace-nowrap`} 
                style={{ fontSize: '1.1rem' }}
              >
                {t('nav.about')}
              </Link>
              <Link 
                to="/contact" 
                className={`${location.pathname === '/contact' ? 'text-gray-900' : 'text-gray-400'} hover:text-black transition-colors whitespace-nowrap`} 
                style={{ fontSize: '1.1rem' }}
              >
                {t('nav.contact')}
              </Link>
              {(settings.adminMenuVisible || user) && (
                <Link 
                  to="/admin" 
                  className={`${location.pathname === '/admin' ? 'text-gray-900' : 'text-gray-300'} hover:text-black transition-colors whitespace-nowrap`} 
                  style={{ fontSize: '1.1rem' }}
                >
                  {t('nav.admin')}
                </Link>
              )}
            </nav>
            
            {/* Language Switcher - only show if English is enabled */}
            {settings.englishEnabled && (
              <div className="flex items-center gap-0">
                <button
                  onClick={() => setLanguage('hu')}
                  className={`px-1 transition-colors ${
                    language === 'hu' ? 'text-black' : 'text-gray-400 hover:text-black'
                  }`}
                  style={{ fontSize: '1.1rem', fontWeight: 300 }}
                >
                  HU
                </button>
                <button
                  onClick={() => setLanguage('en')}
                  className={`px-1 transition-colors ${
                    language === 'en' ? 'text-black' : 'text-gray-400 hover:text-black'
                  }`}
                  style={{ fontSize: '1.1rem', fontWeight: 300 }}
                >
                  EN
                </button>
              </div>
            )}
          </div>

          {/* Mobile Language Switcher & Menu Button */}
          <div className="md:hidden flex items-center gap-3">
            {/* Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
              className="p-0"
              style={{ display: 'flex', alignItems: 'center', verticalAlign: 'middle', transform: 'translateY(-2px)' }}
            >
              {isMenuOpen ? <X size={24} style={{ display: 'block' }} /> : <Menu size={24} style={{ display: 'block' }} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200">
          <nav className="flex flex-col px-4 py-4 gap-4">
            {settings.newsEnabled && (
              <Link 
                to="/news" 
                onClick={() => setIsMenuOpen(false)} 
                className={`text-left py-2 ${location.pathname === '/news' ? 'text-gray-900' : 'text-gray-400'} hover:text-black transition-colors`}
              >
                {t('nav.news')}
              </Link>
            )}
            
            <div className="flex flex-col">
              <Link 
                to="/" 
                onClick={() => {
                  setSelectedCategory('all');
                  setIsMenuOpen(false);
                }} 
                className={`text-left py-2 ${location.pathname === '/' ? 'text-gray-900' : 'text-gray-400'} hover:text-black transition-colors`}
              >
                {t('nav.projects')}
              </Link>
              {/* Mobile Category Filters */}
              <div className="pl-4 flex flex-col gap-1 mt-2">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => {
                      handleCategoryClick(category.id);
                      setIsMenuOpen(false);
                    }}
                    className="text-left py-2 text-sm text-gray-400 hover:text-black transition-colors uppercase"
                  >
                    {category.name[language]}
                  </button>
                ))}
              </div>
            </div>
            
            <Link 
              to="/about" 
              onClick={() => setIsMenuOpen(false)} 
              className={`text-left py-2 ${location.pathname === '/about' ? 'text-gray-900' : 'text-gray-400'} hover:text-black transition-colors`}
            >
              {t('nav.about')}
            </Link>
            <Link 
              to="/contact" 
              onClick={() => setIsMenuOpen(false)} 
              className={`text-left py-2 ${location.pathname === '/contact' ? 'text-gray-900' : 'text-gray-400'} hover:text-black transition-colors`}
            >
              {t('nav.contact')}
            </Link>
            {(settings.adminMenuVisible || user) && (
              <Link 
                to="/admin" 
                onClick={() => setIsMenuOpen(false)} 
                className={`text-left py-2 ${location.pathname === '/admin' ? 'text-gray-900' : 'text-gray-300'} hover:text-black transition-colors`}
              >
                {t('nav.admin')}
              </Link>
            )}
            
            {/* Language Switcher - only show if English is enabled */}
            {settings.englishEnabled && (
              <div className="flex items-center gap-2 py-2 border-t border-gray-200 mt-2 pt-4">
                <button
                  onClick={() => {
                    setLanguage('hu');
                    setIsMenuOpen(false);
                  }}
                  className={`px-3 py-1 transition-colors ${
                    language === 'hu' ? 'text-black' : 'text-gray-400 hover:text-black'
                  }`}
                  style={{ fontSize: '1rem', fontWeight: 300 }}
                >
                  HU
                </button>
                <span className="text-gray-300">|</span>
                <button
                  onClick={() => {
                    setLanguage('en');
                    setIsMenuOpen(false);
                  }}
                  className={`px-3 py-1 transition-colors ${
                    language === 'en' ? 'text-black' : 'text-gray-400 hover:text-black'
                  }`}
                  style={{ fontSize: '1rem', fontWeight: 300 }}
                >
                  EN
                </button>
              </div>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}